import { useEffect, useMemo, useState } from 'react'
import { api } from '../api'
import type { CalView } from '../lib/dates'
import {
  addDays,
  monthMatrix,
  monthLong,
  parseYmd,
  rangeFor,
  startOfWeek,
  todayYmd,
  navAnchor,
  viewTitle,
  weekdayLabels,
  weekdayNarrow,
  ymd,
} from '../lib/dates'
import type { DaySummary, JobRole } from '../types'
import { useI18n } from '../i18n'
import { RAG_META } from '../lib/rag'
import { CoverageBar, RagDot, Spinner } from '../components/ui'
import { IconChevronLeft, IconChevronRight } from '../components/icons'
import DayView from '../components/DayView'

const VIEWS: CalView[] = ['day', 'week', 'month', 'quarter', 'year']

export default function CalendarPage() {
  const { t } = useI18n()
  const [view, setView] = useState<CalView>('week')
  const [anchor, setAnchor] = useState<Date>(() => parseYmd(todayYmd()))
  const [summaries, setSummaries] = useState<Map<string, DaySummary>>(new Map())
  const [loading, setLoading] = useState(false)
  const [reload, setReload] = useState(0)
  const [roles, setRoles] = useState<JobRole[]>([])
  const [selectedRoles, setSelectedRoles] = useState<string[]>([])

  const range = useMemo(() => rangeFor(view, anchor), [view, anchor])

  useEffect(() => {
    api
      .roles()
      .then((r) => setRoles(r.roles))
      .catch(() => {})
  }, [])

  const roleKey = selectedRoles.join(',')

  useEffect(() => {
    if (view === 'day') return // DayView loads its own detail
    let active = true
    setLoading(true)
    api
      .calendar(range.from, range.to, selectedRoles.length ? selectedRoles : undefined)
      .then((res) => {
        if (!active) return
        const map = new Map<string, DaySummary>()
        for (const d of res.days) map.set(d.date, d)
        setSummaries(map)
      })
      .catch(() => {})
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [view, range.from, range.to, reload, roleKey])

  const toggleRole = (id: string) =>
    setSelectedRoles((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]))

  const pickDay = (dateStr: string) => {
    setAnchor(parseYmd(dateStr))
    setView('day')
  }
  const pickMonth = (year: number, month: number) => {
    setAnchor(new Date(year, month, 1))
    setView('month')
  }

  return (
    <div>
      <div className="page-head">
        <h1>{t('cal.title')}</h1>
        <p>{t('cal.desc')}</p>
      </div>

      <div className="cal-toolbar">
        <div className="row gap-16 wrap">
          <div className="cal-nav">
            <button className="iconbtn" onClick={() => setAnchor((a) => navAnchor(view, a, -1))} aria-label={t('common.prev')}>
              <IconChevronLeft />
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setAnchor(parseYmd(todayYmd()))}>
              {t('common.today')}
            </button>
            <button className="iconbtn" onClick={() => setAnchor((a) => navAnchor(view, a, 1))} aria-label={t('common.next')}>
              <IconChevronRight />
            </button>
          </div>
          <div className="cal-title">
            {viewTitle(view, anchor)}
            {loading && <span style={{ marginLeft: 12, verticalAlign: 'middle' }}><Spinner /></span>}
          </div>
        </div>
        <div className="row gap-16 wrap">
          <Legend />
          <div className="segmented">
            {VIEWS.map((v) => (
              <button key={v} className={view === v ? 'active' : ''} onClick={() => setView(v)}>
                {t('view.' + v)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {roles.length > 0 && (
        <div className="row gap-8 wrap" style={{ marginBottom: 16 }}>
          <button
            type="button"
            className={`badge${selectedRoles.length === 0 ? ' purple' : ' grey'}`}
            style={{ cursor: 'pointer', border: 'none' }}
            onClick={() => setSelectedRoles([])}
          >
            {t('roles.all')}
          </button>
          {roles.map((r) => {
            const on = selectedRoles.includes(r.id)
            return (
              <button
                key={r.id}
                type="button"
                className="badge"
                style={{
                  cursor: 'pointer',
                  border: on ? `1.5px solid ${r.color}` : '1.5px solid transparent',
                  background: on ? r.color : 'var(--hover)',
                  color: on ? '#fff' : 'var(--muted)',
                }}
                onClick={() => toggleRole(r.id)}
              >
                {r.name}
              </button>
            )
          })}
        </div>
      )}

      {view === 'day' && (
        <DayView date={ymd(anchor)} onBack={() => setView('week')} onChanged={() => setReload((r) => r + 1)} />
      )}
      {view === 'week' && <WeekView anchor={anchor} summaries={summaries} onPick={pickDay} />}
      {view === 'month' && <MonthView anchor={anchor} summaries={summaries} onPick={pickDay} />}
      {view === 'quarter' && (
        <QuarterView anchor={anchor} summaries={summaries} onPickDay={pickDay} onPickMonth={pickMonth} />
      )}
      {view === 'year' && (
        <YearView anchor={anchor} summaries={summaries} onPickDay={pickDay} onPickMonth={pickMonth} />
      )}
    </div>
  )
}

function Legend() {
  const { t } = useI18n()
  return (
    <div className="legend">
      <span className="item"><span className="rag-dot green" /> {t('legend.covered')}</span>
      <span className="item"><span className="rag-dot amber" /> {t('legend.partly')}</span>
      <span className="item"><span className="rag-dot red" /> {t('legend.needs')}</span>
    </div>
  )
}

/* -------------------------------- Week -------------------------------- */

function WeekView({
  anchor,
  summaries,
  onPick,
}: {
  anchor: Date
  summaries: Map<string, DaySummary>
  onPick: (d: string) => void
}) {
  const { t } = useI18n()
  const start = startOfWeek(anchor)
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i))
  const dow = weekdayLabels()
  return (
    <div className="week-grid">
      {days.map((d, i) => {
        const key = ymd(d)
        const s = summaries.get(key)
        const meta = s ? RAG_META[s.rag] : RAG_META.none
        return (
          <button key={key} className={`week-col${key === todayYmd() ? ' today' : ''}`} onClick={() => onPick(key)}>
            <div className="wc-head">
              <div>
                <div className="wc-dow">{dow[i]}</div>
                <div className="wc-num">{d.getDate()}</div>
              </div>
              {s?.open && <RagDot rag={s.rag} />}
            </div>
            <div className="wc-body">
              {s?.open ? (
                <>
                  <span className="badge" style={{ background: meta.soft, color: meta.ink }}>
                    {t('rag.' + s.rag)}
                  </span>
                  <CoverageBar rag={s.rag} pct={Math.round(s.coverage * 100)} />
                  <div className="hour-sub">
                    {t('cal.hrs', { covered: s.covered, required: s.required })}
                    {s.pending ? ` · ${t('cal.pending', { n: s.pending })}` : ''}
                  </div>
                </>
              ) : (
                <div className="hour-sub">{t('cal.noShifts')}</div>
              )}
            </div>
          </button>
        )
      })}
    </div>
  )
}

/* -------------------------------- Month ------------------------------- */

function MonthView({
  anchor,
  summaries,
  onPick,
}: {
  anchor: Date
  summaries: Map<string, DaySummary>
  onPick: (d: string) => void
}) {
  const { t } = useI18n()
  const weeks = monthMatrix(anchor.getFullYear(), anchor.getMonth())
  return (
    <div>
      <div className="dow-head">
        {weekdayLabels().map((w) => (
          <span key={w}>{w}</span>
        ))}
      </div>
      <div className="month-grid">
        {weeks.flat().map((d) => {
          const key = ymd(d)
          const inMonth = d.getMonth() === anchor.getMonth()
          const s = summaries.get(key)
          return (
            <button
              key={key}
              className={`day-cell${inMonth ? '' : ' dim'}${key === todayYmd() ? ' today' : ''}`}
              onClick={() => onPick(key)}
            >
              {s?.open && <span className={`stripe ${s.rag}`} />}
              <div className="day-num">{d.getDate()}</div>
              {s?.open && (
                <>
                  <div className="cover">{t('cal.coveredPct', { pct: Math.round(s.coverage * 100) })}</div>
                  <div className="needpill">
                    <span className={`badge ${s.rag}`}>{t('rag.short.' + s.rag)}</span>
                  </div>
                </>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/* ----------------------------- Mini month ----------------------------- */

function MiniMonth({
  year,
  month,
  summaries,
  onPickDay,
  onPickMonth,
}: {
  year: number
  month: number
  summaries: Map<string, DaySummary>
  onPickDay: (d: string) => void
  onPickMonth: (y: number, m: number) => void
}) {
  const { t } = useI18n()
  const weeks = monthMatrix(year, month)
  const dow = weekdayNarrow()
  return (
    <div className="mini">
      <h4 onClick={() => onPickMonth(year, month)}>{monthLong(month)}</h4>
      <div className="mini-dow">
        {dow.map((w, i) => (
          <span key={i}>{w}</span>
        ))}
      </div>
      <div className="mini-days">
        {weeks.flat().map((d) => {
          const key = ymd(d)
          const inMonth = d.getMonth() === month
          const s = summaries.get(key)
          const rag = inMonth && s?.open ? s.rag : ''
          return (
            <button
              key={key}
              className={`mini-day ${inMonth ? '' : 'dim'} ${rag} ${key === todayYmd() ? 'today' : ''}`}
              onClick={() => onPickDay(key)}
              title={inMonth && s?.open ? `${t('rag.' + s.rag)} · ${Math.round(s.coverage * 100)}%` : undefined}
            >
              {d.getDate()}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function QuarterView({
  anchor,
  summaries,
  onPickDay,
  onPickMonth,
}: {
  anchor: Date
  summaries: Map<string, DaySummary>
  onPickDay: (d: string) => void
  onPickMonth: (y: number, m: number) => void
}) {
  const startMonth = Math.floor(anchor.getMonth() / 3) * 3
  const months = [startMonth, startMonth + 1, startMonth + 2]
  return (
    <div className="mini-grid q">
      {months.map((m) => (
        <MiniMonth
          key={m}
          year={anchor.getFullYear()}
          month={m}
          summaries={summaries}
          onPickDay={onPickDay}
          onPickMonth={onPickMonth}
        />
      ))}
    </div>
  )
}

function YearView({
  anchor,
  summaries,
  onPickDay,
  onPickMonth,
}: {
  anchor: Date
  summaries: Map<string, DaySummary>
  onPickDay: (d: string) => void
  onPickMonth: (y: number, m: number) => void
}) {
  return (
    <div className="mini-grid y">
      {Array.from({ length: 12 }, (_, m) => (
        <MiniMonth
          key={m}
          year={anchor.getFullYear()}
          month={m}
          summaries={summaries}
          onPickDay={onPickDay}
          onPickMonth={onPickMonth}
        />
      ))}
    </div>
  )
}
